import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ConfigService } from '../../services/config-service.js';

/**
 * Debug Wheel Viewer Component
 * 
 * This component provides a dedicated debug view that uses the EXACT same
 * wheel and activity components as the main UI, ensuring perfect debugging
 * accuracy. It allows loading wheels via debug API and testing wheel
 * interactions in isolation.
 */
@customElement('debug-wheel-viewer')
export class DebugWheelViewer extends LitElement {
  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      background: #f8f9fa;
      border-radius: 8px;
      padding: 16px;
      box-sizing: border-box;
    }

    .debug-viewer-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      gap: 16px;
    }

    .debug-controls {
      background: white;
      border-radius: 6px;
      padding: 12px;
      border: 1px solid #e9ecef;
    }

    .wheel-container {
      flex: 1;
      display: flex;
      gap: 16px;
      min-height: 400px;
    }

    .wheel-display {
      flex: 1;
      background: white;
      border-radius: 6px;
      padding: 16px;
      border: 1px solid #e9ecef;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .activity-panel {
      width: 300px;
      background: white;
      border-radius: 6px;
      padding: 16px;
      border: 1px solid #e9ecef;
      overflow-y: auto;
    }

    .control-group {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .control-group label {
      font-weight: 500;
      min-width: 80px;
    }

    .btn {
      padding: 6px 12px;
      border: 1px solid #dee2e6;
      border-radius: 4px;
      background: white;
      cursor: pointer;
      font-size: 12px;
      transition: all 0.2s;
    }

    .btn:hover {
      background: #f8f9fa;
      border-color: #adb5bd;
    }

    .btn.primary {
      background: #007bff;
      color: white;
      border-color: #007bff;
    }

    .btn.primary:hover {
      background: #0056b3;
      border-color: #0056b3;
    }

    .btn.danger {
      background: #dc3545;
      color: white;
      border-color: #dc3545;
    }

    .btn.danger:hover {
      background: #c82333;
      border-color: #c82333;
    }

    .status-display {
      padding: 8px;
      border-radius: 4px;
      font-size: 11px;
      margin-top: 8px;
    }

    .status-success {
      background: #d4edda;
      color: #155724;
      border: 1px solid #c3e6cb;
    }

    .status-error {
      background: #f8d7da;
      color: #721c24;
      border: 1px solid #f5c6cb;
    }

    .status-info {
      background: #d1ecf1;
      color: #0c5460;
      border: 1px solid #bee5eb;
    }

    .no-wheel-message {
      text-align: center;
      color: #6c757d;
      font-style: italic;
      padding: 40px;
    }

    select {
      padding: 4px 8px;
      border: 1px solid #ced4da;
      border-radius: 4px;
      font-size: 12px;
      min-width: 200px;
    }

    .debug-info {
      font-size: 10px;
      color: #6c757d;
      margin-top: 8px;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
    }
  `;

  @property({ type: Boolean }) visible = false;
  
  @state() private availableWheels: Array<{id: number, name: string, created_by: string, created_at: string, item_count: number}> = [];
  @state() private selectedWheelId = '';
  @state() private currentWheel: any = null;
  @state() private isLoading = false;
  @state() private status = '';
  @state() private statusType: 'success' | 'error' | 'info' = 'info';

  private configService = ConfigService.getInstance();

  private getApiBaseUrl(): string {
    return this.configService.getConfig().api.baseUrl;
  }

  async firstUpdated() {
    if (this.visible) {
      await this.loadAvailableWheels();
    }
  }

  private async loadAvailableWheels() {
    this.isLoading = true;
    this.status = 'Loading available wheels...';
    this.statusType = 'info';
    
    try {
      const baseUrl = this.getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/debug/wheels/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        this.availableWheels = data.wheels || [];
        this.status = `Found ${this.availableWheels.length} wheels available for debugging`;
        this.statusType = 'success';
      } else {
        this.status = 'Failed to load wheels - check admin authentication';
        this.statusType = 'error';
      }
    } catch (error) {
      console.error('Error loading wheels:', error);
      this.status = 'Error loading wheels - check network connection';
      this.statusType = 'error';
    } finally {
      this.isLoading = false;
    }
  }

  private handleWheelSelection(event: Event) {
    const target = event.target as HTMLSelectElement;
    this.selectedWheelId = target.value;
  }

  private async loadSelectedWheel() {
    if (!this.selectedWheelId) {
      this.status = 'Please select a wheel first';
      this.statusType = 'error';
      return;
    }

    this.isLoading = true;
    this.status = 'Loading wheel...';
    this.statusType = 'info';
    
    try {
      const baseUrl = this.getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/debug/wheels/${this.selectedWheelId}/load/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        this.currentWheel = data.wheel_data;
        this.status = `✅ Loaded: ${data.message}`;
        this.statusType = 'success';
        
        console.log('🎡 DEBUG WHEEL VIEWER: Loaded wheel:', this.currentWheel);
      } else {
        const errorData = await response.json();
        this.status = `❌ ${errorData.error || 'Failed to load wheel'}`;
        this.statusType = 'error';
      }
    } catch (error) {
      console.error('Error loading wheel:', error);
      this.status = '❌ Error loading wheel';
      this.statusType = 'error';
    } finally {
      this.isLoading = false;
    }
  }

  private clearWheel() {
    this.currentWheel = null;
    this.selectedWheelId = '';
    this.status = 'Wheel cleared';
    this.statusType = 'info';
  }

  private async testWheelItemRemoval() {
    if (!this.currentWheel || !this.currentWheel.items || this.currentWheel.items.length === 0) {
      this.status = 'No wheel items available for removal testing';
      this.statusType = 'error';
      return;
    }

    const firstItem = this.currentWheel.items[0];
    this.status = `Testing removal of item: ${firstItem.name}`;
    this.statusType = 'info';
    
    // This will test the exact same removal logic as the main UI
    // We'll dispatch the same event that the main UI uses
    this.dispatchEvent(new CustomEvent('debug-test-removal', {
      detail: { 
        wheelId: this.currentWheel.id,
        itemId: firstItem.id,
        wheelData: this.currentWheel
      },
      bubbles: true
    }));
  }

  render() {
    if (!this.visible) return html``;

    return html`
      <div class="debug-viewer-container">
        <div class="debug-controls">
          <h3 style="margin: 0 0 12px 0; font-size: 14px;">🎡 Debug Wheel Viewer</h3>
          
          <div class="control-group">
            <label>Wheel:</label>
            <select .value=${this.selectedWheelId} @change=${this.handleWheelSelection} ?disabled=${this.isLoading}>
              <option value="">Select a wheel...</option>
              ${this.availableWheels.map(wheel => html`
                <option value=${wheel.id}>
                  Wheel ${wheel.id}: ${wheel.name} (${wheel.item_count} items)
                </option>
              `)}
            </select>
            <button class="btn" @click=${this.loadAvailableWheels} ?disabled=${this.isLoading}>
              🔄 Refresh
            </button>
          </div>

          <div class="control-group">
            <button class="btn primary" @click=${this.loadSelectedWheel} ?disabled=${this.isLoading || !this.selectedWheelId}>
              ${this.isLoading ? '⏳ Loading...' : '🎡 Load Wheel'}
            </button>
            <button class="btn" @click=${this.clearWheel} ?disabled=${!this.currentWheel}>
              🗑️ Clear
            </button>
            <button class="btn danger" @click=${this.testWheelItemRemoval} ?disabled=${!this.currentWheel}>
              🧪 Test Item Removal
            </button>
          </div>

          ${this.status ? html`
            <div class="status-display status-${this.statusType}">
              ${this.status}
            </div>
          ` : ''}
        </div>

        <div class="wheel-container">
          <div class="wheel-display">
            ${this.currentWheel ? html`
              <!-- Use the EXACT same game-wheel component as main UI -->
              <game-wheel 
                .wheelData=${this.currentWheel}
                .isSpinning=${false}
                .canSpin=${true}
                @wheel-item-selected=${this.handleWheelItemSelected}
              ></game-wheel>
            ` : html`
              <div class="no-wheel-message">
                No wheel loaded. Select a wheel from the dropdown above.
              </div>
            `}
          </div>

          <div class="activity-panel">
            <h4 style="margin: 0 0 12px 0; font-size: 13px;">Activity List</h4>
            ${this.currentWheel && this.currentWheel.items ? html`
              <!-- Use the EXACT same activity list rendering as main UI -->
              ${this.currentWheel.items.map((item: any) => html`
                <div class="activity-item" style="padding: 8px; border: 1px solid #e9ecef; border-radius: 4px; margin-bottom: 8px;">
                  <div style="font-weight: 500; font-size: 12px; margin-bottom: 4px;">
                    ${item.name}
                  </div>
                  <div style="font-size: 10px; color: #6c757d; margin-bottom: 4px;">
                    ID: ${item.id} | ${item.percentage}%
                  </div>
                  <div style="font-size: 11px; color: #495057;">
                    ${item.description}
                  </div>
                  <button class="btn danger" style="margin-top: 4px; font-size: 10px;" 
                          @click=${() => this.testItemRemoval(item)}>
                    🗑️ Test Remove
                  </button>
                </div>
              `)}
            ` : html`
              <div style="color: #6c757d; font-style: italic; font-size: 11px;">
                No activities to display
              </div>
            `}
          </div>
        </div>

        ${this.currentWheel ? html`
          <div class="debug-info">
            <strong>Debug Info:</strong> Wheel ID: ${this.currentWheel.id}, Items: ${this.currentWheel.items?.length || 0}, 
            Using exact same components as main UI for accurate debugging.
          </div>
        ` : ''}
      </div>
    `;
  }

  private handleWheelItemSelected(event: CustomEvent) {
    console.log('🎡 DEBUG: Wheel item selected:', event.detail);
    this.status = `Selected: ${event.detail.item?.name || 'Unknown item'}`;
    this.statusType = 'info';
  }

  private testItemRemoval(item: any) {
    this.status = `Testing removal of: ${item.name} (ID: ${item.id})`;
    this.statusType = 'info';
    
    // Dispatch the same event as main UI for testing
    this.dispatchEvent(new CustomEvent('debug-test-item-removal', {
      detail: { 
        wheelId: this.currentWheel.id,
        itemId: item.id,
        item: item,
        wheelData: this.currentWheel
      },
      bubbles: true
    }));
  }
}
